import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["badge", "panel", "overlay", "list", "trigger", "bellIcon", "closeIcon"]
  static values = { count: Number, open: { type: Boolean, default: false } }

  connect() {
    console.log(`[notifications] connect: count=${this.countValue}, open=${this.openValue}, has{panel,overlay,list,badge}=${this.hasPanelTarget}/${this.hasOverlayTarget}/${this.hasListTarget}/${this.hasBadgeTarget}`)
    // Force the drawer to its hidden default on connect so a previously-
    // cached open state can't survive a navigation. Without this, the
    // legacy data-turbo-permanent path could leave the drawer visible.
    if (this.hasPanelTarget) {
      this.panelTarget.classList.add("translate-x-full")
      this.panelTarget.classList.remove("translate-x-0")
    }
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("hidden")
    this.openValue = false
    this._applyTriggerState()

    // Listen for Mercure-pushed badge count updates
    document.addEventListener("turbo:before-stream-render", this._onBadgeStream)
  }

  async toggle() {
    this.openValue = !this.openValue
    console.log(`[notifications] toggle → open=${this.openValue}`)
    this.updatePanel()

    if (this.openValue && this.hasListTarget) {
      await this.loadNotifications()
      this._scheduleWaterfall()
    } else {
      this._cancelWaterfall()
    }
  }

  close() {
    console.log(`[notifications] close`)
    this._cancelWaterfall()
    this.openValue = false
    this.updatePanel()
  }

  updatePanel() {
    if (!this.hasPanelTarget) {
      console.warn(`[notifications] updatePanel called but no panel target`)
      return
    }

    if (this.openValue) {
      console.log(`[notifications] open: sliding drawer in`)
      this.panelTarget.classList.remove("translate-x-full")
      this.panelTarget.classList.add("translate-x-0")
      if (this.hasOverlayTarget) this.overlayTarget.classList.remove("hidden")
      document.addEventListener("keydown", this._onEscape)
      // Deferred so the bell-click that opened the drawer doesn't immediately close it.
      setTimeout(() => document.addEventListener("click", this._onOutsideClick), 0)
    } else {
      console.log(`[notifications] hide: sliding drawer out`)
      this.panelTarget.classList.add("translate-x-full")
      this.panelTarget.classList.remove("translate-x-0")
      if (this.hasOverlayTarget) this.overlayTarget.classList.add("hidden")
      document.removeEventListener("keydown", this._onEscape)
      document.removeEventListener("click", this._onOutsideClick)
    }

    this._applyTriggerState()
  }

  _applyTriggerState() {
    if (this.openValue) {
      console.log(`[notifications] trigger → close`)
      if (this.hasBellIconTarget) {
        this.bellIconTarget.classList.remove("rotate-0", "opacity-100", "scale-100")
        this.bellIconTarget.classList.add("rotate-90", "opacity-0", "scale-75", "pointer-events-none")
      }
      if (this.hasCloseIconTarget) {
        this.closeIconTarget.classList.remove("-rotate-90", "opacity-0", "scale-75", "pointer-events-none")
        this.closeIconTarget.classList.add("rotate-0", "opacity-100", "scale-100")
      }
      if (this.hasTriggerTarget) {
        this.triggerTarget.setAttribute("aria-expanded", "true")
        this.triggerTarget.setAttribute("aria-label", "Close notifications")
      }
    } else {
      console.log(`[notifications] trigger → bell`)
      if (this.hasBellIconTarget) {
        this.bellIconTarget.classList.remove("rotate-90", "opacity-0", "scale-75", "pointer-events-none")
        this.bellIconTarget.classList.add("rotate-0", "opacity-100", "scale-100")
      }
      if (this.hasCloseIconTarget) {
        this.closeIconTarget.classList.remove("rotate-0", "opacity-100", "scale-100")
        this.closeIconTarget.classList.add("-rotate-90", "opacity-0", "scale-75", "pointer-events-none")
      }
      if (this.hasTriggerTarget) {
        this.triggerTarget.setAttribute("aria-expanded", "false")
        this.triggerTarget.setAttribute("aria-label", "Open notifications")
      }
    }
  }

  async loadNotifications() {
    if (!this.hasListTarget) {
      console.warn(`[notifications] loadNotifications called but no list target`)
      return
    }

    const url = "/activity/notifications"
    console.log(`[notifications] fetching: ${url}`)
    try {
      const response = await fetch(url, {
        headers: { "Accept": "text/html" }
      })
      console.log(`[notifications] response: ${response.status} ${response.statusText}`)
      if (response.ok) {
        const html = await response.text()
        this.listTarget.innerHTML = html
        console.log(`[notifications] loaded ${html.length} bytes`)
      } else {
        console.error(`[notifications] non-OK response: ${response.status}`)
      }
    } catch (e) {
      console.error(`[notifications] fetch failed:`, e)
    }
  }

  async markSeen(event) {
    const id = event.currentTarget.dataset.eventId
    if (!id) return

    try {
      const csrfToken = document.querySelector("meta[name=csrf-token]")?.content
      await fetch(`/activity/${id}/seen`, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        }
      })

      const item = event.currentTarget

      // Remove unseen indicator
      const dot = item.querySelector("[data-unseen-dot]")
      if (dot) dot.classList.add("hidden")

      // Remove bold from title
      const title = item.querySelector("[data-notification-title]")
      if (title) title.classList.remove("font-medium")

      // Mark as seen in DOM so waterfall skips it
      item.dataset.seen = "true"

      // Hide progress bar if active
      const bar = item.querySelector("[data-waterfall-bar]")
      if (bar) { bar.classList.remove("active"); bar.style.opacity = "0" }

      // Decrement badge
      this.countValue = Math.max(0, this.countValue - 1)
      this.updateBadge()
    } catch (e) {
      console.error("[notifications] Failed to mark seen:", e)
    }
  }

  async markAllSeen() {
    this._cancelWaterfall()

    // Immediate loading state
    const btn = this.element.querySelector("[data-action*='markAllSeen']")
    const originalText = btn?.textContent
    if (btn) { btn.textContent = "Marking..."; btn.disabled = true }

    try {
      const csrfToken = document.querySelector("meta[name=csrf-token]")?.content
      const res = await fetch("/activity/mark_all_seen", {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        redirect: "manual"  // Don't follow 302 redirects
      })

      // Handle both JSON response (fixed) and redirect (legacy)
      if (res.type === "opaqueredirect" || res.status === 302) {
        // Legacy redirect — still mark as success client-side
      } else if (res.ok) {
        // Proper JSON response
      }

      // Hide all unseen dots
      this.element.querySelectorAll("[data-unseen-dot]").forEach(dot => {
        dot.classList.add("hidden")
      })

      // Remove bold from all titles
      this.element.querySelectorAll("[data-notification-title]").forEach(title => {
        title.classList.remove("font-medium")
      })

      // Hide any active progress bars
      this.element.querySelectorAll("[data-waterfall-bar]").forEach(bar => {
        bar.classList.remove("active")
        bar.style.opacity = "0"
      })

      // Mark all items as seen in DOM
      this.element.querySelectorAll("[data-seen]").forEach(item => {
        item.dataset.seen = "true"
      })

      // Clear badge
      this.countValue = 0
      this.updateBadge()

      // Update button to done state
      if (btn) { btn.textContent = "All read"; btn.disabled = false }
    } catch (e) {
      console.error("[notifications] Failed to mark all seen:", e)
      // Restore button on error
      if (btn) { btn.textContent = originalText || "Mark all read"; btn.disabled = false }
    }
  }

  updateBadge() {
    if (!this.hasBadgeTarget) return

    if (this.countValue > 0) {
      this.badgeTarget.classList.remove("hidden")
      this.badgeTarget.textContent = this.countValue > 9 ? "9+" : this.countValue
    } else {
      this.badgeTarget.classList.add("hidden")
      this.badgeTarget.textContent = ""
    }
  }

  // ── Waterfall auto-mark-read ──────────────────────────────

  _scheduleWaterfall() {
    this._cancelWaterfall()
    this._waterfallAbort = new AbortController()
    const signal = this._waterfallAbort.signal

    setTimeout(() => {
      if (signal.aborted) return
      this._runWaterfall(signal)
    }, 200)
  }

  _cancelWaterfall() {
    if (this._waterfallAbort) {
      this._waterfallAbort.abort()
      this._waterfallAbort = null
    }
  }

  _runWaterfall(signal) {
    const items = [...this.element.querySelectorAll("[data-seen='false']")]
    if (items.length === 0) return

    let index = 0

    const processNext = () => {
      if (signal.aborted || index >= items.length) return

      const item = items[index]
      // Skip items that were manually clicked during waterfall
      if (item.dataset.seen === "true") {
        index++
        processNext()
        return
      }

      index++
      this._animateItem(item, signal, processNext)
    }

    processNext()
  }

  _animateItem(item, signal, onComplete) {
    const bar = item.querySelector("[data-waterfall-bar]")
    if (!bar) { onComplete(); return }

    // Start progress bar animation
    bar.classList.add("active")

    // After progress completes
    setTimeout(() => {
      if (signal.aborted) return

      // Fire PATCH request (fire-and-forget)
      const eventId = item.dataset.eventId
      if (eventId) {
        const csrfToken = document.querySelector("meta[name=csrf-token]")?.content
        fetch(`/activity/${eventId}/seen`, {
          method: "PATCH",
          headers: {
            "X-CSRF-Token": csrfToken,
            "Accept": "application/json"
          }
        }).catch(e => console.error("[notifications] mark seen failed:", e))
      }

      // Hide unseen dot
      const dot = item.querySelector("[data-unseen-dot]")
      if (dot) dot.classList.add("hidden")

      // Remove bold from title
      const title = item.querySelector("[data-notification-title]")
      if (title) title.classList.remove("font-medium")

      // Mark as seen in DOM
      item.dataset.seen = "true"

      // Decrement badge
      this.countValue = Math.max(0, this.countValue - 1)
      this.updateBadge()

      // Hide progress bar (keep at full width, fade out)
      bar.classList.remove("active")
      bar.style.width = "100%"
      bar.style.opacity = "0"

      // Celebration pulse
      item.classList.add("notif-read-celebrate")

      // After celebration fades, process next
      setTimeout(() => {
        item.classList.remove("notif-read-celebrate")
        if (!signal.aborted) onComplete()
      }, 100)

    }, 150)
  }

  _onEscape = (event) => {
    if (event.key === "Escape") this.close()
  }

  _onOutsideClick = (event) => {
    if (!this.hasPanelTarget) return
    if (this.panelTarget.contains(event.target)) return
    if (event.target.closest("[data-action*='notifications#toggle']")) return
    console.log(`[notifications] outside click → close`)
    this.close()
  }

  _onBadgeStream = (event) => {
    const stream = event.detail?.newStream
    if (!stream || stream.target !== "notification-badge-count") return

    const templateEl = stream.templateElement
    if (!templateEl) return

    event.preventDefault()

    const count = parseInt(templateEl.innerHTML.trim(), 10)
    if (isNaN(count)) return

    this.countValue = count
    this.updateBadge()
  }

  disconnect() {
    console.log(`[notifications] disconnect`)
    this._cancelWaterfall()
    document.removeEventListener("keydown", this._onEscape)
    document.removeEventListener("click", this._onOutsideClick)
    document.removeEventListener("turbo:before-stream-render", this._onBadgeStream)
  }
}
